import { RTCSession } from 'jssip/lib/RTCSession';
import { callUser, hangupCall } from './camlin-voice';
import { setupSipMessageHandler, cleanupSipMessageHandler } from './sipMessageHandler';
import type { UA, WebSocketInterface } from 'jssip';
import JsSIP from 'jssip';

import { ensurePlaybackAudioContextRunning } from '../../core/utils/playbackAudioContext';
import { getCachedTurnCredentials } from './turnCredentialsClient';
import { createSipRegistrationRetryCap } from './sipRegistrationRetryCap';
import { getCachedMicStream } from './micStreamHolder';

// Voice call host - use environment variable or fallback to sip.camlinconnect.com.au
const defaultCvHost = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_VOICE_HOST) ||
  (typeof window !== 'undefined' && (window as any).CAMLIN_VOICE_HOST) ||
  'sip.camlinconnect.com.au';
const defaultUsername = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SIP_USERNAME) ||
  (typeof window !== 'undefined' && (window as any).CAMLIN_SIP_USERNAME) ||
  'webrtc-user';
const defaultPassword = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SIP_PASSWORD) ||
  (typeof window !== 'undefined' && (window as any).CAMLIN_SIP_PASSWORD) ||
  '';

// ICE/TURN credentials are now fetched dynamically by the
// `useTurnCredentials` hook (core/hooks/useTurnCredentials.ts), which calls
// runtime-service `/api/turn-credentials` and writes into
// `turnCredentialsClient`. The hook runs in `useHostedLiveController` so
// every avatar consumer (DM, AgentLink, builder) gets fresh creds without
// any consumer-side change.
//
// Pre-Apr-30-2026 this file held three hardcoded long-lived Twilio NTS
// `username`/`credential` strings that had silently expired. The
// production symptom was: attempt 1 with `iceTransportPolicy: 'all'` could
// limp along on direct/host candidates if the user's network was open;
// attempt 2 with `iceTransportPolicy: 'relay'` (the relay-only retry)
// failed 100% with no candidates → SIP 488 → "Live voice connection lost".
// The static creds were the root cause of the headline live-mode
// reliability complaint. They have been removed entirely. STUN-only is
// the only fallback if the dynamic fetch hasn't resolved.

export interface VoiceCallRegistrationExhaustedDetail {
  username: string
  attempts: number
  cause?: string
}

export interface VoiceCallConfig {
  host?: string
  username?: string
  password?: string
  webSocketUrl?: string
  onRegistrationChange?: (ready: boolean) => void
  onRegistrationExhausted?: (detail: VoiceCallRegistrationExhaustedDetail) => void
}

export interface MakeCallOptions {
  extraHeaders?: string[]
  iceTransportPolicy?: RTCIceTransportPolicy
  audioElementId?: string
  /**
   * Per-call ICE server override. Skipped in production (the
   * `useTurnCredentials` hook populates a module-level cache that
   * `makeCall` reads from). Tests pass a static array here so they
   * don't have to spin up the runtime-service `/api/turn-credentials`
   * endpoint or mock window.fetch.
   */
  iceServers?: RTCIceServer[]
  onSessionStateChange?: (
    state: VoiceCallSessionState,
    detail?: VoiceCallSessionStateDetail
  ) => void
}

export type VoiceCallSessionState =
  | 'connecting'
  | 'progress'
  | 'accepted'
  | 'confirmed'
  | 'audio-ready'
  | 'connection-lost'
  | 'ended'
  | 'failed'

export interface VoiceCallSessionStateDetail {
  cause?: string
  reason?: string
  iceConnectionState?: RTCIceConnectionState
  connectionState?: RTCPeerConnectionState
  recoverable?: boolean
}

const PREFERRED_AUDIO_CODECS = new Set(['PCMU', 'PCMA'])
const TELEPHONY_DTMF_CLOCK_RATE = '8000'

interface AudioPayloadDescriptor {
  codec: string
  clockRate: string
}

// Strip ICE candidates Asterisk can never use. The Asterisk security group
// only opens UDP 10000-20000 (no TCP), so any TCP candidate the browser
// offers is guaranteed to time out during ICE checks. Each timed-out
// pair adds seconds to the overall convergence — observed 20-30s ICE
// "checking" → "connected" transitions on prod calls.
//
// We also strip mDNS-obfuscated candidates (`*.local`) when the offer
// is going to a non-mDNS-aware peer (Asterisk). Those candidates can't
// be resolved server-side and just generate failed checks.
//
// Returns the SDP unchanged if no offending candidates are present.
function filterUnreachableIceCandidatesInSdp(sdp: string): string {
  const lines = sdp.split(/\r?\n/)
  let removed = 0

  const filtered = lines.filter((line) => {
    if (!line.startsWith('a=candidate:')) {
      return true
    }
    const fields = line.split(/\s+/)
    // a=candidate:foundation component transport priority ip port typ ...
    // index:    0                  1         2         3        4  5    6
    const transport = (fields[2] ?? '').toLowerCase()
    const ip = fields[4] ?? ''

    if (transport === 'tcp' || transport === 'tls') {
      removed += 1
      return false
    }
    if (ip.endsWith('.local')) {
      removed += 1
      return false
    }
    return true
  })

  if (removed === 0) {
    return sdp
  }

  console.log(`Stripped ${removed} unreachable ICE candidate(s) from local SDP (TCP/mDNS)`)
  return filtered.join('\r\n')
}

function filterLocalAudioCodecsInSdp(sdp: string): string {
  const lines = sdp.split(/\r?\n/)
  let audioStart = -1
  let audioEnd = lines.length

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    if (line.startsWith('m=audio ')) {
      audioStart = index
      continue
    }
    if (audioStart !== -1 && line.startsWith('m=')) {
      audioEnd = index
      break
    }
  }

  if (audioStart === -1) {
    return sdp
  }

  const audioSection = lines.slice(audioStart, audioEnd)
  const payloadDescriptors = new Map<string, AudioPayloadDescriptor>()

  for (const line of audioSection) {
    const match = /^a=rtpmap:(\d+)\s+([^/]+)\/(\d+)/i.exec(line)
    if (!match) {
      continue
    }
    payloadDescriptors.set(match[1], {
      codec: match[2].toUpperCase(),
      clockRate: match[3]
    })
  }

  const mLineParts = lines[audioStart].trim().split(/\s+/)
  if (mLineParts.length < 4) {
    return sdp
  }

  const advertisedPayloads = mLineParts.slice(3)
  const preferredPayloads = advertisedPayloads.filter((payload) => (
    PREFERRED_AUDIO_CODECS.has(payloadDescriptors.get(payload)?.codec ?? '')
  ))
  const passthroughPayloads = advertisedPayloads.filter((payload) => {
    const desc = payloadDescriptors.get(payload)
    return desc?.codec === 'TELEPHONE-EVENT'
      && desc.clockRate === TELEPHONY_DTMF_CLOCK_RATE
  })

  if (preferredPayloads.length === 0) {
    return sdp
  }

  const allowedPayloads = new Set([...preferredPayloads, ...passthroughPayloads])
  const filteredSection = [
    [...mLineParts.slice(0, 3), ...preferredPayloads, ...passthroughPayloads].join(' ')
  ]

  for (let index = audioStart + 1; index < audioEnd; index += 1) {
    const line = lines[index]
    const rtpmapMatch = /^a=rtpmap:(\d+)\s+/i.exec(line)
    if (rtpmapMatch && !allowedPayloads.has(rtpmapMatch[1])) {
      continue
    }

    const payloadMatch = /^a=(?:fmtp|rtcp-fb):(\d+)\b/i.exec(line)
    if (payloadMatch && !allowedPayloads.has(payloadMatch[1])) {
      continue
    }

    filteredSection.push(line)
  }

  lines.splice(audioStart, audioEnd - audioStart, ...filteredSection)
  return lines.join('\r\n')
}

function notifyAudioAccessRequired(message: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent('avatar:audio-access-required', {
    detail: { message }
  }));
}

function getRemoteAudioElement(audioElementId?: string): HTMLAudioElement | null {
  return document.getElementById(audioElementId || 'remoteAudio') as HTMLAudioElement | null;
}

function normalizeCallTarget(ua: UA, target: string): string {
  const trimmedTarget = target.trim()
  if (!trimmedTarget) {
    return trimmedTarget
  }

  if (/^[a-z]+:/i.test(trimmedTarget)) {
    return trimmedTarget
  }

  if (trimmedTarget.includes('@')) {
    return `sip:${trimmedTarget}`
  }

  const configuredUri =
    (ua as any).configuration?.uri ||
    (ua as any)._configuration?.uri

  const configuredHost =
    configuredUri?.host ||
    configuredUri?._host ||
    (typeof configuredUri?.toString === 'function'
      ? configuredUri.toString().split('@')[1]
      : undefined) ||
    defaultCvHost.split(':')[0]

  return `sip:${trimmedTarget}@${configuredHost}`
}

function applyPreferredAudioCodecs(pc: RTCPeerConnection) {
  try {
    const allCodecs = RTCRtpReceiver.getCapabilities('audio')?.codecs ?? []
    const preferredCodecs = allCodecs.filter((codec) =>
      codec.mimeType === 'audio/PCMU' || codec.mimeType === 'audio/PCMA'
    )

    if (preferredCodecs.length === 0) {
      return
    }

    for (const transceiver of pc.getTransceivers()) {
      if (transceiver.receiver.track?.kind === 'audio' || transceiver.sender.track?.kind === 'audio') {
        transceiver.setCodecPreferences(preferredCodecs)
      }
    }

    console.log('Applied audio codec preferences:', preferredCodecs.map((codec) => codec.mimeType).join(', '))
  } catch (error) {
    console.warn('Unable to set audio codec preferences:', error)
  }
}

export function initializeVoiceCall(config: VoiceCallConfig = {}): { ua: UA; stop: () => void } {
  console.log('Initializing voice call');
  const host = config.host || defaultCvHost;
  const registrationHost = host.includes(':') ? host.split(':')[0] : host;
  const username = config.username || defaultUsername;
  const password = config.password || defaultPassword;
  const isLocalSipHost = registrationHost === 'localhost' || registrationHost === '127.0.0.1';
  const webSocketUrl = config.webSocketUrl || (
    isLocalSipHost
      ? `ws://${host.includes(':') ? host : `${host}:8088`}/ws`
      : `wss://${registrationHost}/ws`
  );
  const socket: WebSocketInterface = new JsSIP.WebSocketInterface(webSocketUrl);
  let isShuttingDown = false;
  const activeSessions = new Set<RTCSession>();
  let registrationRetryTimer: ReturnType<typeof setTimeout> | null = null;
  const registrationRetry = createSipRegistrationRetryCap();

  const configuration = {
    sockets: [socket],
    uri: `sip:${username}@${registrationHost}`,
    password: password,
    trace_sip: false,
    session_timers: false,
    hack_ip_in_contact: true,
    register: true,
    register_expires: 300,
    connection_recovery_min_interval: 2,
    connection_recovery_max_interval: 30,
    use_preloaded_route: false,
    registrar_server: `sip:${registrationHost}`,
    contact_uri: `sip:${username}@${registrationHost}`,
    sockets_options: {
      ws: {
        maxReconnectionAttempts: 3,
        reconnectionTimeout: 4,
        via_transport: isLocalSipHost ? 'WS' : 'WSS',
        keepAliveInterval: 30,
        keepAliveDebounce: 10,
        traceSip: false,
        wsServers: [webSocketUrl]
      }
    },
    realm: 'asterisk'
  };

  let ua: UA = new JsSIP.UA(configuration);

  const clearRegistrationRetryTimer = () => {
    if (registrationRetryTimer !== null) {
      clearTimeout(registrationRetryTimer);
      registrationRetryTimer = null;
    }
  };

  const canRecoverConnection = () =>
    !isShuttingDown && registrationRetry.shouldRecoverConnection();

  const disableFurtherRegistration = () => {
    try {
      const cfg = (ua as any)._configuration || (ua as any).configuration;
      if (cfg) {
        cfg.register = false;
      }
    } catch (error) {
      console.debug('Unable to disable further SIP REGISTER attempts:', error);
    }
  };

  ua.on('disconnected', (event: any) => {
    console.log('UA Disconnected - checking shutdown state');
    config.onRegistrationChange?.(false);
    if (!canRecoverConnection()) {
      return;
    }
    console.log('Not shutting down, attempting reconnection...');
    setTimeout(() => {
      if (!canRecoverConnection()) {
        return;
      }
      try {
        if ((socket as any).readyState !== WebSocket.OPEN) {
          (socket as any).connect();
        }
        if (!ua.isRegistered()) {
          ua.register();
        }
      } catch (e) {
        console.error('Error in reconnection attempt:', e);
      }
    }, 3000);
  });

  const stop = () => {
    if (isShuttingDown) {
      return;
    }
    console.log('Stopping UA...');
    isShuttingDown = true;
    clearRegistrationRetryTimer();
    disableFurtherRegistration();
    config.onRegistrationChange?.(false);
    try {
      if (ua.isRegistered()) {
        ua.unregister();
      }
      ua.stop();
      if ((socket as any).readyState === WebSocket.OPEN) {
        (socket as any).disconnect();
      }
    } catch (error) {
      console.error('Error during stop:', error);
    }
  };

  const giveUpRegistration = (cause?: string) => {
    const attempts = registrationRetry.consecutiveFailures;
    const detail: VoiceCallRegistrationExhaustedDetail = {
      username,
      attempts,
      cause
    };
    console.error(
      `[voiceCall] SIP REGISTER loop stopped after ${attempts} failures for ${username}` +
        `${cause ? ` (${cause})` : ''} — leftover UA will not keep retrying`
    );
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('avatar:voice-call-state', {
        detail: {
          state: 'failed',
          reason: 'sip-registration-exhausted',
          cause,
          recoverable: false
        }
      }));
    }
    config.onRegistrationChange?.(false);
    config.onRegistrationExhausted?.(detail);
    disableFurtherRegistration();
    clearRegistrationRetryTimer();
    if (activeSessions.size === 0) {
      stop();
    }
  };

  ua.on('newRTCSession', (data: any) => {
    const session: RTCSession = data.session;
    activeSessions.add(session);

    const releaseSession = () => {
      activeSessions.delete(session);
      if (registrationRetry.givenUp && activeSessions.size === 0 && !isShuttingDown) {
        stop();
      }
    };

    session.on('ended', releaseSession);
    session.on('failed', releaseSession);

    session.connection.oniceconnectionstatechange = () => {
      console.log('ICE Connection State:', session.connection.iceConnectionState);
    };

    session.connection.onicegatheringstatechange = () => {
      console.log('ICE Gathering State:', session.connection.iceGatheringState);
    };

    session.on('connecting', () => {
      console.log('WebRTC session connecting');
    });

    session.on('progress', () => {
      console.log('WebRTC session in progress');
    });

    session.on('accepted', () => {
      console.log('WebRTC session accepted');
    });

    session.on('confirmed', () => {
      console.log('WebRTC session confirmed');
    });

    session.on('ended', () => {
      console.log('WebRTC session ended');
    });

    session.on('failed', (e: any) => {
      console.error('WebRTC session failed:', e);
    });
  });

  ua.on('connected', () => {
    // Do NOT reset isShuttingDown here. Leftover UAs with register:true +
    // connection_recovery reconnect the WebSocket, fire `connected`, and
    // would otherwise REGISTER-loop all day after a missing endpoint.
    if (!canRecoverConnection()) {
      console.log('UA Connected after registration give-up / shutdown — not re-registering');
      return;
    }
    console.log('UA Connected - attempting registration');
    try {
      ua.register()
    } catch (e) {
      console.error('Error registering after connect:', e);
    }
  });

  ua.on('registered', (event: any) => {
    console.log('UA Registered successfully', event);
    registrationRetry.recordRegistered();
    config.onRegistrationChange?.(true);
  });

  ua.on('registrationFailed', (event: any) => {
    config.onRegistrationChange?.(false);
    const cause = typeof event?.cause === 'string' ? event.cause : undefined;
    const decision = registrationRetry.recordRegistrationFailed();
    if (decision.kind === 'give-up') {
      giveUpRegistration(cause);
      return;
    }

    // SIP registration failure is expected in local dev without voice infrastructure.
    // After the cap, leftover UAs stop so voice logs do not fill all day.
    if (cause === 'Connection Error') {
      console.debug(
        `[voiceCall] SIP registration unavailable (attempt ${decision.failureCount}/${decision.maxFailures}):`,
        cause
      );
    } else {
      console.warn(
        `[voiceCall] SIP registration failed (attempt ${decision.failureCount}/${decision.maxFailures}):`,
        cause ?? event
      );
    }
    if (!canRecoverConnection()) {
      return;
    }
    clearRegistrationRetryTimer();
    registrationRetryTimer = setTimeout(() => {
      registrationRetryTimer = null;
      if (!canRecoverConnection()) {
        return;
      }
      try {
        if ((socket as any).readyState !== WebSocket.OPEN) {
          (socket as any).connect();
        }
        ua.register();
      } catch (e) {
        console.debug('SIP registration retry failed (expected in local dev):', e);
      }
    }, decision.delayMs);
  });

  ua.on('unregistered', (event: any) => {
    console.log('UA Unregistered', event);
    config.onRegistrationChange?.(false);
    if (!canRecoverConnection()) {
      return;
    }
    setTimeout(() => {
      if (!canRecoverConnection()) {
        return;
      }
      try {
        if ((socket as any).readyState !== WebSocket.OPEN) {
          (socket as any).connect();
        }
        ua.register();
      } catch (e) {
        console.error('Error in unregistered handler:', e);
      }
    }, 3000);
  });

  (socket as any).onopen = () => {
    console.log('WebSocket opened');
    const keepAliveInterval = setInterval(() => {
      if ((socket as any).readyState === WebSocket.OPEN && canRecoverConnection()) {
        (socket as any).send('\r\n\r\n');
      } else {
        clearInterval(keepAliveInterval);
      }
    }, 30000);
  };

  (socket as any).onerror = (error: any) => {
    console.error('WebSocket Error:', error);
    if (!canRecoverConnection()) {
      return;
    }
    setTimeout(() => {
      if (!canRecoverConnection()) {
        return;
      }
      try {
        (socket as any).connect();
      } catch (e) {
        console.error('Error reconnecting socket:', e);
      }
    }, 5000);
  };

  (socket as any).onclose = (event: any) => {
    console.log('WebSocket Closed:', event);
    if (!canRecoverConnection()) {
      return;
    }
    setTimeout(() => {
      if (!canRecoverConnection()) {
        return;
      }
      try {
        (socket as any).connect();
      } catch (e) {
        console.error('Error reconnecting after close:', e);
      }
    }, 5000);
  };

  try {
    ua.start();
    console.log('UA started successfully');
  } catch (e) {
    console.error('Error starting UA:', e);
  }

  return { ua, stop };
}

export function setupVoiceCallHandlers(uaObject: { ua: UA; stop: () => void }, handleEvent: (eventName: string, data: any, props?: any) => void) {
  if (!uaObject || !uaObject.ua) {
    console.warn('Invalid UA object provided to setupVoiceCallHandlers');
    return () => { }; // Return empty cleanup function
  }

  const { ua, stop } = uaObject;

  try {
    // Clean up any existing handlers first
    cleanupSipMessageHandler(ua);
    // Setup new handler
    setupSipMessageHandler(ua, handleEvent);
    console.log('SIP message handlers set up');
  } catch (error) {
    console.error('Error setting up SIP message handler:', error);
  }

  return () => {
    try {
      cleanupSipMessageHandler(ua);
      if (stop) {
        stop();
      }
    } catch (error) {
      console.error('Error cleaning up voice call handlers:', error);
    }
  };
}

export function makeCall(ua: UA, target: string, options: MakeCallOptions = {}): any {
  console.log('Making call to:', target, 'with UA:', ua);
  const normalizedTarget = normalizeCallTarget(ua, target)

  // Mirror every voice call state transition onto a window event so
  // `useAvatarHealth` can flip the status pip to red on 'failed' /
  // 'connection-lost' and back to green on 'confirmed' / 'audio-ready'
  // without having to be wired into every consumer's onSessionStateChange
  // chain. Callers' existing onSessionStateChange handler still fires
  // first — the mirror is purely additive observability.
  //
  // Why a wrapper instead of dispatching at each call site:
  //   The original `options.onSessionStateChange` is invoked from ~7
  //   places (connecting/progress/accepted/confirmed/ended/failed plus
  //   reportConnectionLoss). Wrapping once keeps the mirror a single
  //   line of code that can't drift out of sync with the real handler.
  const originalOnSessionStateChange = options.onSessionStateChange
  options = {
    ...options,
    onSessionStateChange: (state, detail) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('avatar:voice-call-state', {
            detail: { state, ...(detail ?? {}) }
          })
        );
      }
      originalOnSessionStateChange?.(state, detail);
    }
  }

  // Resolve ICE servers in priority order:
  //   1. Caller-supplied `options.iceServers` (test override / future
  //      per-call customisation). No SDK consumer uses this today, but
  //      keeping the option means the dynamic-fetch path isn't load-bearing
  //      for tests.
  //   2. Module-level cache populated by the `useTurnCredentials` hook.
  //      This is the production path — fresh ~4h Twilio NTS tokens minted
  //      by runtime-service `/api/turn-credentials`.
  //   3. STUN-only fallback baked into `turnCredentialsClient` (with a
  //      one-time console.warn). Browsers behind symmetric NAT will
  //      fail to negotiate WebRTC in this state — exactly the regression
  //      we're fixing — but at least the failure is loud and traceable
  //      rather than the previous silent expired-token failure.
  const resolvedIceServers: RTCIceServer[] = options.iceServers
    ?? getCachedTurnCredentials().iceServers
  const cachedMic = getCachedMicStream()
  const callOptions = {
    mediaConstraints: { audio: true, video: false },
    ...(cachedMic ? { mediaStream: cachedMic } : {}),
    pcConfig: {
      iceServers: resolvedIceServers,
      iceTransportPolicy: options.iceTransportPolicy ?? 'all'
    },
    rtcOfferConstraints: {
      offerToReceiveAudio: true,
      offerToReceiveVideo: false
    },
    extraHeaders: [
      'X-Device-Type: mobile',
      'X-Browser: ' + navigator.userAgent,
      ...(options.extraHeaders ?? [])
    ]
  };

  let session: RTCSession;
  try {
    // @ts-ignore
    session = ua.call(normalizedTarget, callOptions);
    console.log('Call initiated successfully');
  } catch (e) {
    console.error('Error initiating call:', e);
    throw e;
  }

  // Add audio setup function
  let audioSetupAttempt = 0;
  let audioRetryListenerBound = false;
  let lastAudioReadyTrackIds = '';
  let clearAudioRetryListeners: (() => void) | null = null;
  let cleanupAudioRecoveryListeners: (() => void) | null = null;
  let fallbackAudioSourceNode: MediaStreamAudioSourceNode | null = null;
  let fallbackAudioTrackIds = '';

  const cleanupAudioRetryInteraction = () => {
    clearAudioRetryListeners?.();
    clearAudioRetryListeners = null;
  };

  const clearAudioRecoveryListeners = () => {
    cleanupAudioRecoveryListeners?.();
    cleanupAudioRecoveryListeners = null;
  };

  const disconnectAudioFallback = () => {
    try {
      fallbackAudioSourceNode?.disconnect();
    } catch (error) {
      console.warn('Unable to disconnect Web Audio fallback cleanly:', error);
    }

    fallbackAudioSourceNode = null;
    fallbackAudioTrackIds = '';
  };

  // Defer audio-ready until at least one remote audio track is actually
  // unmuted. WebRTC marks freshly-attached tracks as muted=true and only
  // unmutes them once real RTP packets start arriving — which on slow ICE
  // paths (Asterisk in Fargate behind NLB, browser host candidates timing
  // out, etc.) can happen 20–30 seconds after the SDP "confirmed" event.
  // If we fire audio-ready prematurely, the consumer transitions the UI to
  // "listening" while the user still can't hear anything for half a minute.
  //
  // Strategy:
  //   1. When playRemoteAudio attaches the stream, queue the track ids.
  //   2. If any track is already unmuted, fire audio-ready immediately.
  //   3. Otherwise, wait for the first unmute event from the bound peer
  //      connection's pc.ontrack handler (which calls markAudioReady again
  //      via setupAudio after track.onunmute fires).
  //   4. As a safety net, if 30s passes and we still haven't unmuted, fire
  //      audio-ready anyway so the consumer doesn't hang forever — better
  //      a brief audio gap than a stuck "Connecting" state.
  const AUDIO_READY_FALLBACK_MS = 30_000
  let audioReadyFallbackTimer: ReturnType<typeof setTimeout> | null = null

  const clearAudioReadyFallbackTimer = () => {
    if (audioReadyFallbackTimer !== null) {
      clearTimeout(audioReadyFallbackTimer)
      audioReadyFallbackTimer = null
    }
  }

  const fireAudioReady = (trackIds: string, reason: 'unmuted' | 'fallback' | 'already-flowing') => {
    if (!trackIds || lastAudioReadyTrackIds === trackIds) {
      return
    }
    lastAudioReadyTrackIds = trackIds
    clearAudioReadyFallbackTimer()
    if (reason !== 'unmuted') {
      console.warn(`audio-ready fired (${reason}) — emitting state to consumer`)
    }
    options.onSessionStateChange?.('audio-ready')
  }

  const markAudioReady = (trackIds: string) => {
    if (!trackIds || lastAudioReadyTrackIds === trackIds) {
      return
    }

    const stream = remoteStreamForTracks(trackIds)
    const tracks = stream ? stream.getAudioTracks() : []
    const anyUnmuted = tracks.some((track) => track.muted === false)

    if (anyUnmuted) {
      fireAudioReady(trackIds, 'already-flowing')
      return
    }

    clearAudioReadyFallbackTimer()
    audioReadyFallbackTimer = setTimeout(() => {
      audioReadyFallbackTimer = null
      fireAudioReady(trackIds, 'fallback')
    }, AUDIO_READY_FALLBACK_MS)
  };

  // Lookup helper: find the MediaStream attached to the remote audio
  // element whose track ids match the given joined string. Used by
  // markAudioReady to decide whether to gate on unmute.
  const remoteStreamForTracks = (trackIds: string): MediaStream | null => {
    const remoteAudio = getRemoteAudioElement(options.audioElementId)
    if (!remoteAudio || !(remoteAudio.srcObject instanceof MediaStream)) {
      return null
    }
    const currentIds = remoteAudio.srcObject.getAudioTracks().map((t) => t.id).sort().join(',')
    return currentIds === trackIds ? remoteAudio.srcObject : null
  }

  const registerAudioRetryAfterInteraction = (remoteAudio: HTMLAudioElement, trackIds: string) => {
    if (audioRetryListenerBound) {
      return;
    }

    audioRetryListenerBound = true;

    const cleanupRetryListeners = () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      audioRetryListenerBound = false;
      clearAudioRetryListeners = null;
    };

    const handleRetry = async () => {
      cleanupRetryListeners();
      try {
        const playPromise = remoteAudio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }

        disconnectAudioFallback();
        markAudioReady(trackIds);
        console.log('Audio playback started after user interaction');
      } catch (e) {
        console.error('Error playing audio after interaction:', e);
      }
    };

    const handlePointerDown = () => {
      void handleRetry();
    };

    const handleKeyDown = () => {
      void handleRetry();
    };

    clearAudioRetryListeners = cleanupRetryListeners;
    document.addEventListener('pointerdown', handlePointerDown, { once: true, capture: true });
    document.addEventListener('keydown', handleKeyDown, { once: true, capture: true });
  };

  const resolvedAudioElementId = options.audioElementId;

  const setupAudio = async (stream: MediaStream) => {
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn('Skipping audio setup - stream has no audio tracks');
      return;
    }

    const remoteAudio = getRemoteAudioElement(resolvedAudioElementId);
    if (!remoteAudio) {
      console.error('Remote audio element not found');
      return;
    }

    const setupAttemptId = ++audioSetupAttempt;
    const nextTrackIds = audioTracks.map((track) => track.id).sort().join(',');

    const playRemoteAudio = async (reason: string) => {
      if (setupAttemptId !== audioSetupAttempt) {
        return;
      }

      try {
        const playPromise = remoteAudio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }

        if (setupAttemptId !== audioSetupAttempt) {
          return;
        }

        cleanupAudioRetryInteraction();
        disconnectAudioFallback();
        markAudioReady(nextTrackIds);
        console.log('Audio playback started successfully');
      } catch (error) {
        const errorName = error instanceof DOMException
          ? error.name
          : typeof (error as { name?: unknown })?.name === 'string'
            ? String((error as { name: string }).name)
            : '';
        const errorMessage = error instanceof Error ? error.message : String(error ?? '');
        const isAbortError = errorName === 'AbortError' || errorMessage.includes('interrupted by a new load request');
        const wasSuperseded = setupAttemptId !== audioSetupAttempt;

        if (isAbortError || wasSuperseded) {
          console.warn(`Ignoring remote audio play interruption during ${reason}`, {
            errorName,
            errorMessage,
            wasSuperseded
          });
          return;
        }

        try {
          const audioContext = await ensurePlaybackAudioContextRunning();
          if (audioContext && audioContext.state === 'running') {
            if (!fallbackAudioSourceNode || fallbackAudioTrackIds !== nextTrackIds) {
              disconnectAudioFallback();
              fallbackAudioSourceNode = audioContext.createMediaStreamSource(stream);
              fallbackAudioSourceNode.connect(audioContext.destination);
              fallbackAudioTrackIds = nextTrackIds;
            }

            cleanupAudioRetryInteraction();
            markAudioReady(nextTrackIds);
            console.warn(`Routing remote audio through Web Audio fallback (${reason})`, {
              errorName,
              errorMessage
            });
            return;
          }
        } catch (fallbackError) {
          console.warn('Unable to activate Web Audio fallback for remote stream:', fallbackError);
        }

        throw error;
      }
    };

    try {
      const currentTrackIds = remoteAudio.srcObject instanceof MediaStream
        ? remoteAudio.srcObject.getAudioTracks().map((track) => track.id).sort().join(',')
        : '';
      const alreadyAttached = nextTrackIds.length > 0 && nextTrackIds === currentTrackIds;

      clearAudioRecoveryListeners();

      if (!alreadyAttached) {
        disconnectAudioFallback();
        remoteAudio.srcObject = stream;
      } else {
        console.log('Remote audio stream already attached, reusing element');
      }
      remoteAudio.volume = 1.0;
      remoteAudio.autoplay = true;
      remoteAudio.playsInline = true;
      remoteAudio.preload = 'auto';
      remoteAudio.defaultMuted = false;

      // Ensure proper attributes
      remoteAudio.setAttribute('playsinline', '');
      remoteAudio.setAttribute('webkit-playsinline', 'true');
      remoteAudio.setAttribute('autoplay', '');
      remoteAudio.setAttribute('preload', 'auto');
      remoteAudio.muted = false;

      const retryPlayback = (reason: string, blockedMessage: string) => {
        void playRemoteAudio(reason).catch((e) => {
          console.error(`Error playing audio on ${reason}:`, e);
          notifyAudioAccessRequired(blockedMessage);
          registerAudioRetryAfterInteraction(remoteAudio, nextTrackIds);
        });
      };

      const handleLoadedMetadata = () => {
        retryPlayback(
          'metadata',
          'Audio playback was blocked when the live call stream became ready. Allow audio playback for this page, then retry.'
        );
      };

      const handleCanPlay = () => {
        if (remoteAudio.paused) {
          retryPlayback(
            'canplay',
            'Audio playback was blocked when the live call stream was ready to play. Allow audio playback for this page, then retry.'
          );
        }
      };

      const handlePause = () => {
        if (sessionTerminated || setupAttemptId !== audioSetupAttempt) {
          return;
        }
        if (!(remoteAudio.srcObject instanceof MediaStream) || remoteAudio.srcObject.getAudioTracks().length === 0) {
          return;
        }

        console.warn('Remote audio paused unexpectedly - retrying');
        retryPlayback(
          'pause',
          'Audio playback paused after the live call started. Allow audio playback for this page, then retry.'
        );
      };

      const handlePlaying = () => {
        disconnectAudioFallback();
      };

      const handleWindowRecovery = () => {
        if (sessionTerminated || setupAttemptId !== audioSetupAttempt) {
          return;
        }
        if (document.visibilityState === 'hidden') {
          return;
        }
        if (!(remoteAudio.srcObject instanceof MediaStream) || remoteAudio.srcObject.getAudioTracks().length === 0) {
          return;
        }
        if (!remoteAudio.paused) {
          disconnectAudioFallback();
          return;
        }

        retryPlayback(
          'window-recovery',
          'Audio playback was paused when the tab became active again. Allow audio playback for this page, then retry.'
        );
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          handleWindowRecovery();
        }
      };

      remoteAudio.addEventListener('loadedmetadata', handleLoadedMetadata);
      remoteAudio.addEventListener('canplay', handleCanPlay);
      remoteAudio.addEventListener('pause', handlePause);
      remoteAudio.addEventListener('playing', handlePlaying);
      window.addEventListener('focus', handleWindowRecovery);
      window.addEventListener('pageshow', handleWindowRecovery);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      cleanupAudioRecoveryListeners = () => {
        remoteAudio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        remoteAudio.removeEventListener('canplay', handleCanPlay);
        remoteAudio.removeEventListener('pause', handlePause);
        remoteAudio.removeEventListener('playing', handlePlaying);
        window.removeEventListener('focus', handleWindowRecovery);
        window.removeEventListener('pageshow', handleWindowRecovery);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };

      if (remoteAudio.paused || !alreadyAttached) {
        await playRemoteAudio('initial attach');
      }
    } catch (error) {
      console.error('Error setting up audio:', error);
      notifyAudioAccessRequired('Audio playback is currently blocked for this page. Allow audio playback, then retry access or press Listen again.');
      registerAudioRetryAfterInteraction(remoteAudio, audioTracks.map((track) => track.id).sort().join(','));
    }
  };

  let boundPeerConnection: RTCPeerConnection | null = null;
  let disconnectTimer: ReturnType<typeof window.setTimeout> | null = null;
  let connectionLossReported = false;
  let sessionTerminated = false;

  const clearDisconnectTimer = () => {
    if (!disconnectTimer) {
      return;
    }

    window.clearTimeout(disconnectTimer);
    disconnectTimer = null;
  };

  const reportConnectionLoss = (
    reason: string,
    detail: VoiceCallSessionStateDetail,
    recoverable = true
  ) => {
    if (connectionLossReported) {
      return;
    }

    connectionLossReported = true;
    options.onSessionStateChange?.('connection-lost', {
      ...detail,
      reason,
      recoverable
    });
  };

  const getRemoteAudioStream = (pc: RTCPeerConnection | null | undefined): MediaStream | null => {
    if (!pc) {
      return null;
    }

    const receiverTracks = pc.getReceivers()
      .map((receiver) => receiver.track)
      .filter((track): track is MediaStreamTrack => Boolean(track) && track.kind === 'audio');

    if (receiverTracks.length > 0) {
      return new MediaStream(receiverTracks);
    }

    const remoteStreams = session.connection?.getRemoteStreams?.() ?? [];
    const streamWithAudio = remoteStreams.find((stream) => stream.getAudioTracks().length > 0);
    return streamWithAudio ?? null;
  };

  const bindPeerConnection = (pc: RTCPeerConnection | null | undefined) => {
    if (!pc || pc === boundPeerConnection) {
      return;
    }

    boundPeerConnection = pc;
    applyPreferredAudioCodecs(pc);
    console.log('Binding remote audio handlers to peer connection', {
      signalingState: pc.signalingState,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState
    });

    pc.ontrack = (event: RTCTrackEvent) => {
      console.log('Track added:', {
        kind: event.track.kind,
        id: event.track.id,
        muted: event.track.muted,
        enabled: event.track.enabled,
        readyState: event.track.readyState,
        streams: event.streams.map((stream) => ({
          id: stream.id,
          audioTracks: stream.getAudioTracks().length
        }))
      });

      if (event.track.kind !== 'audio') {
        return;
      }

      event.track.onmute = () => console.warn('Remote audio track muted');
      event.track.onunmute = () => {
        console.log('Remote audio track unmuted');
        // First real RTP frame just arrived. If we previously deferred
        // the audio-ready signal in markAudioReady (because the track
        // was muted at the time), fire it now so the consumer can
        // transition out of "Connecting voice…" exactly when audio
        // becomes audible. setupAudio re-runs the attachment path which
        // re-evaluates markAudioReady with the now-unmuted track.
        void setupAudio(stream);
      };
      event.track.onended = () => console.warn('Remote audio track ended');

      const streamWithAudio = event.streams.find((stream) => stream.getAudioTracks().length > 0);
      const stream = streamWithAudio ?? new MediaStream([event.track]);
      setupAudio(stream);
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      if (sessionTerminated) {
        clearDisconnectTimer();
        return;
      }

      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        connectionLossReported = false;
        clearDisconnectTimer();
        return;
      }

      if (pc.iceConnectionState === 'failed') {
        clearDisconnectTimer();
        try {
          pc.restartIce?.();
        } catch (error) {
          console.warn('ICE restart attempt failed:', error);
        }
        reportConnectionLoss('ice-failed', {
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState
        }, false);
        return;
      }

      if (pc.iceConnectionState === 'disconnected') {
        clearDisconnectTimer();
        disconnectTimer = window.setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
            reportConnectionLoss('ice-disconnected', {
              iceConnectionState: pc.iceConnectionState,
              connectionState: pc.connectionState
            });
          }
        }, 3000);
        return;
      }

      clearDisconnectTimer();
    };

    pc.onconnectionstatechange = () => {
      console.log('Peer connection state:', pc.connectionState);
      if (sessionTerminated) {
        clearDisconnectTimer();
        return;
      }

      if (pc.connectionState === 'connected') {
        connectionLossReported = false;
        clearDisconnectTimer();
        return;
      }

      if (pc.connectionState === 'failed') {
        clearDisconnectTimer();
        reportConnectionLoss('peer-failed', {
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState
        }, false);
        return;
      }

      if (pc.connectionState === 'disconnected') {
        clearDisconnectTimer();
        disconnectTimer = window.setTimeout(() => {
          if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
            reportConnectionLoss('peer-disconnected', {
              iceConnectionState: pc.iceConnectionState,
              connectionState: pc.connectionState
            });
          }
        }, 3000);
        return;
      }

      clearDisconnectTimer();
    };

    pc.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
      if (event.candidate) {
        console.log('New ICE candidate:', event.candidate);
      }
    };
  };

  bindPeerConnection(session.connection as RTCPeerConnection | undefined);

  // Update session handlers
  session.on('peerconnection', (e: any) => {
    console.log('Peer connection event:', e);
    const pc = (e?.peerconnection as RTCPeerConnection | undefined) ?? (session.connection as RTCPeerConnection | undefined);
    bindPeerConnection(pc);
  });

  // SDP munging for local-dev Asterisk-in-Docker. Asterisk inside Docker
  // generates ICE candidates with the container's bridge IP (172.16-31.x.x)
  // which is unreachable from the host browser. We rewrite those addresses
  // in the remote SDP so the browser's RTP socket targets a port-forwarded
  // address on the host. In production this regex never matches anything
  // (Asterisk doesn't run inside Docker bridges), so the SDP passes through
  // untouched.
  //
  // Choice of replacement IP matters. On Mac Docker Desktop, the container
  // exposes RTP on 0.0.0.0:10000-10050. Sending UDP from the browser to
  // 127.0.0.1 reliably hits the host port-forward (which then bridges into
  // the container). Sending to the host's LAN IP works on Linux but on Mac
  // can be blocked by hairpin-NAT routing or firewall — that's the cause of
  // the "ICE checking → disconnected without ever reaching connected" mode
  // we hit before. Default to 127.0.0.1 when the page itself is localhost.
  const browserOnLocalhost = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  let localHostIp = '127.0.0.1'
  let preferLanIp = !browserOnLocalhost
  session.on('sdp', (event: { originator?: string; sdp?: string }) => {
    if (typeof event.sdp !== 'string') {
      return
    }

    if (event.originator === 'local') {
      const candidateMatch = event.sdp.match(
        /a=candidate:\S+ \d+ udp \d+ (\d+\.\d+\.\d+\.\d+) \d+ typ host/i
      )
      if (candidateMatch && preferLanIp) {
        localHostIp = candidateMatch[1]
      }

      // Strip TCP/mDNS ICE candidates Asterisk can never reach.
      // See filterUnreachableIceCandidatesInSdp for rationale.
      const candidateFilteredSdp = filterUnreachableIceCandidatesInSdp(event.sdp)
      if (candidateFilteredSdp !== event.sdp) {
        event.sdp = candidateFilteredSdp
      }

      const filteredSdp = filterLocalAudioCodecsInSdp(event.sdp)
      if (filteredSdp !== event.sdp) {
        console.log('Applied SDP audio codec filter for telephony compatibility')
        event.sdp = filteredSdp
      }
    }

    if (event.originator === 'remote') {
      const replacementIp = localHostIp
      let munged = event.sdp.replace(
        /\b172\.(?:1[6-9]|2[0-9]|3[01])\.\d{1,3}\.\d{1,3}\b/g,
        replacementIp
      )
      if (replacementIp !== '127.0.0.1') {
        munged = munged.replace(
          /^(c=IN IP4 )127\.0\.0\.1$/gm,
          `$1${replacementIp}`
        )
      }
      if (event.sdp !== munged) {
        event.sdp = munged
        console.log(`SDP munged: Docker IP → ${replacementIp}`)
      }
    }
  })

  session.on('connecting', () => {
    console.log('Call connecting');
    options.onSessionStateChange?.('connecting');
  });

  session.on('progress', () => {
    console.log('Call in progress');
    options.onSessionStateChange?.('progress');
  });

  session.on('accepted', () => {
    console.log('Call accepted');
    connectionLossReported = false;
    clearDisconnectTimer();
    options.onSessionStateChange?.('accepted');
    bindPeerConnection(session.connection as RTCPeerConnection | undefined);
    const stream = getRemoteAudioStream(session.connection as RTCPeerConnection | undefined);
    if (stream) {
      setupAudio(stream);
    } else {
      console.warn('No remote audio tracks available at accept time');
    }
  });

  session.on('confirmed', () => {
    console.log('Call confirmed');
    connectionLossReported = false;
    clearDisconnectTimer();
    options.onSessionStateChange?.('confirmed');
    bindPeerConnection(session.connection as RTCPeerConnection | undefined);
    const stream = getRemoteAudioStream(session.connection as RTCPeerConnection | undefined);
    if (stream) {
      setupAudio(stream);
    }
    const remoteAudioConfirm = getRemoteAudioElement(resolvedAudioElementId);
    if (remoteAudioConfirm && remoteAudioConfirm.paused) {
      remoteAudioConfirm.play().catch((e) => {
        console.error('Error playing audio on confirm:', e);
        notifyAudioAccessRequired('Audio playback was blocked after the live call connected. Allow audio playback for this page, then retry.');
        const trackIds = remoteAudioConfirm.srcObject instanceof MediaStream
          ? remoteAudioConfirm.srcObject.getAudioTracks().map((track) => track.id).sort().join(',')
          : '';
        registerAudioRetryAfterInteraction(remoteAudioConfirm, trackIds);
      });
    }
  });

  session.on('ended', (e: any) => {
    const pc = session.connection as RTCPeerConnection | undefined;
    const detail = {
      cause: typeof e?.cause === 'string' ? e.cause : undefined,
      reason: typeof e?.message === 'string' ? e.message : undefined,
      iceConnectionState: pc?.iceConnectionState,
      connectionState: pc?.connectionState,
      recoverable: !connectionLossReported && (pc?.connectionState === 'failed' || pc?.iceConnectionState === 'failed')
    } satisfies VoiceCallSessionStateDetail;
    console.log(`Call ended ${JSON.stringify(detail)}`);
    sessionTerminated = true;
    clearDisconnectTimer();
    clearAudioReadyFallbackTimer();
    cleanupAudioRetryInteraction();
    clearAudioRecoveryListeners();
    disconnectAudioFallback();
    if (!connectionLossReported && (
      pc?.connectionState === 'failed' ||
      pc?.connectionState === 'disconnected' ||
      pc?.iceConnectionState === 'failed' ||
      pc?.iceConnectionState === 'disconnected'
    )) {
      reportConnectionLoss('session-ended-after-peer-failure', detail, true);
    }
    options.onSessionStateChange?.('ended', detail);
    const remoteAudioEnded = getRemoteAudioElement(resolvedAudioElementId);
    if (remoteAudioEnded) {
      remoteAudioEnded.srcObject = null;
      remoteAudioEnded.load();
    }
  });

  session.on('failed', (e: any) => {
    const detail = {
      cause: typeof e?.cause === 'string' ? e.cause : undefined,
      reason: typeof e?.message === 'string' ? e.message : undefined,
      iceConnectionState: (session.connection as RTCPeerConnection | undefined)?.iceConnectionState,
      connectionState: (session.connection as RTCPeerConnection | undefined)?.connectionState
    } satisfies VoiceCallSessionStateDetail;
    console.error(`Call failed ${JSON.stringify(detail)}`);
    sessionTerminated = true;
    clearDisconnectTimer();
    clearAudioReadyFallbackTimer();
    cleanupAudioRetryInteraction();
    clearAudioRecoveryListeners();
    disconnectAudioFallback();
    options.onSessionStateChange?.('failed', detail);
    const remoteAudioFailed = getRemoteAudioElement(resolvedAudioElementId);
    if (remoteAudioFailed) {
      remoteAudioFailed.srcObject = null;
      remoteAudioFailed.load();
    }
  });

  return session;
}

export { callUser, hangupCall };

